<template>
    <div class="mx-auto max-w-screen-lg">
        <div>
            <VerifyEmail
                v-if="user.activated === 0"
                :user="user"
                @verified="verified" />

            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-2">Account Info</h3>
            <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                <div class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6">
                    <dt class="text-sm font-medium text-gray-500 truncate">User ID</dt>
                    <dd class="mt-2 text-3xl font-semibold text-gray-900">{{ state.id }}</dd>
                </div>

                <div class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6">
                    <dt class="text-sm font-medium text-gray-500 truncate">Account ID</dt>
                    <dd class="mt-2 text-3xl font-semibold text-gray-900">{{ state.account_id }}</dd>
                </div>

                <div class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6">
                    <dt class="text-sm font-medium text-gray-500 truncate">Subscription</dt>
                    <dd class="mt-1 text-xl font-semibold text-gray-900">
                        <template v-if="state.account?.subscription">
                            {{ state.account.subscription.package.name }}
                            <p class="text-sm font-normal" v-if="state.account.subscription.cancelled || state.account.subscription.auto_renew == 0"><span class="text-red-700">Ends on:</span> {{ formatDateShort(state.account.subscription.expires) }}</p>
                            <p class="text-sm font-normal" v-else>Renews on: {{ formatDateShort(state.account.subscription.expires) }}</p>
                        </template>
                        <template v-if="!state.account?.subscription">
                            <p class="text-sm font-normal">This customer is not currently enrolled in a subscription package.</p>
                        </template>
                    </dd>
                </div>
            </dl>
        </div>

        <form class="space-y-8 divide-y divide-gray-200" v-if="Object.keys(state).length > 0" @submit.prevent="saveState">
            <div class="space-y-8 divide-y divide-gray-200 sm:space-y-5">
                <div class="pt-8 space-y-6 sm:pt-10 sm:space-y-5">
                    <div>
                        <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-2">Profile Data</h3>
                        <p class="mt-1 max-w-2xl text-sm text-gray-500">Update the customer's profile data</p>
                    </div>
                    <div class="space-y-6 sm:space-y-5">
                        <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                            <label for="name" class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">Name</label>
                            <div class="mt-1 sm:mt-0 sm:col-span-2">
                                <input v-model="state.profile.name" type="text" name="name" id="name" autocomplete="name" class="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md" />
                            </div>
                        </div>

                        <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                            <label for="email" class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">Email address</label>
                            <div class="mt-1 sm:mt-0 sm:col-span-2">
                                <input v-model="state.email" id="email" name="email" type="email" autocomplete="email" class="block max-w-lg w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md" />
                            </div>
                        </div>

                        <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                            <label for="telephone" class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">Telephone</label>
                            <div class="mt-1 sm:mt-0 sm:col-span-2">
                                <input v-model="state.profile.telephone" type="text" name="telephone" id="telephone" autocomplete="tel" class="block max-w-lg w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md" />
                            </div>
                        </div>

                        <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                            <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">Password</label>
                            <div class="mt-1 sm:mt-0 sm:col-span-2">
                                <button type="button" @click="passwordReset"
                                        :class="[passwordHasBeenReset ? 'border-green-400 text-green-800 bg-green-200' : 'border-gray-300 text-gray-700 bg-white']"
                                        :disabled="passwordHasBeenReset"
                                        class="inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    <EnvelopeIcon v-if="!passwordHasBeenReset" class="h-5 w-5 mr-2" />
                                    <CheckCircleIcon v-if="passwordHasBeenReset" class="h-5 w-5 mr-2" />
                                    Reset Password
                                </button>
                            </div>
                        </div>

                        <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                            <label for="name" class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">Email Aliases</label>
                            <div class="mt-1 sm:mt-0 sm:col-span-2">
                                <textarea v-model.trim="state.profile.email_alias" name="email_alias"
                                          class="block max-w-lg w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"/>
                                <i class="block text-gray-800 text-sm mb-2">Separated by comma</i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="pt-5">
                <div class="flex justify-between">
                    <div>
                        <button
                            type="button"
                            @click="deleteUser"
                            class="inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md bg-red-200 hover:bg-red-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 mr-4">
                            <TrashIcon class="h-5 w-5 mr-2" />
                            Delete User
                        </button>
                        <button
                            v-if="user.unsubscribes?.length === 0"
                            type="button"
                            @click="blockUser"
                            class="inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md bg-red-200 hover:bg-red-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                            <NoSymbolIcon class="h-5 w-5 mr-2" />
                            Block User
                        </button>
                        <p
                            v-if="user.unsubscribes?.length > 0"
                            class="text-red-700">
                            User is blocked from receiving mail</p>
                    </div>
                    <button v-if="!loading" type="submit" @click.prevent="saveState" class="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Save
                    </button>
                    <button v-if="loading" type="submit" @click.prevent="" class="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                        Save
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import {ref, inject, toRef, onMounted} from "vue"
import {NoSymbolIcon, CheckCircleIcon, EnvelopeIcon, TrashIcon} from "@heroicons/vue/24/outline"
import {useBus} from "../../../../app"
import VerifyEmail from "./VerifyEmail"
import {useRouter} from "vue-router";

export default {
    name: "Profile",
    components: {
        EnvelopeIcon,
        NoSymbolIcon,
        CheckCircleIcon,
        TrashIcon,
        VerifyEmail,
    },
    props: {
        user: {
            type: Object,
            required: true,
        }
    },
    setup(props, context) {
        const router = useRouter()
        const bus = useBus()
        const alert = inject('alert')
        const loading = ref(false)
        const passwordHasBeenReset = ref(false)

        const state = toRef(props, 'user')

        const processEmailAlias = () => {
            let stateContents = state.value.profile.email_alias;

            if (typeof stateContents === 'string') {
                let email_alias = stateContents.replace(" ", "").split(',')
                let alias_array = []

                email_alias.forEach(function (item) {
                    // Check for valid email address, basic version
                    if (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(item)) {
                        alias_array.push(item)
                    }
                })

                return alias_array
            }

            return state.value.profile.email_alias
        }

        const saveState = async () => {
            let alias = processEmailAlias();

            if (loading.value) {
                return
            } else {
                loading.value = true
            }

            await axios.post(route('users.save', state.value.id), {
                user: state.value,
                profile: { ...state.value.profile, ...{ email_alias: alias } },
            }).then((response) => {
                loading.value = false
                if (response.status === 200) {
                    bus.$emit('alert.success', 'User Updated Successfully')
                    context.emit('refresh', state.value.id)
                }
            }).catch((error) => {
                loading.value = false
                bus.$emit('alert.danger', 'There was a problem, the user was not updated')
            })
        }

        const blockUser = async () => {
            alert.fire({
                title: 'Are you sure?',
                text: 'This will add the user to the "Do Not Send" list',
                icon: 'warning',
                showCancelButton: true,
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('users.block', { user: state.value.id }), {}).then(response => {
                            if (response.data.success === true) {
                                bus.$emit('alert.success', 'Success')
                            } else {
                                bus.$emit('alert.danger', response.data.message)
                            }
                        }).catch(e => {
                            bus.$emit('alert.danger', 'Block failed')
                        })
                    }
                })
        }

        const deleteUser = async () => {
            alert.fire({
                title: 'Are you sure?',
                text: 'This will remove this user from our site completely',
                icon: 'warning',
                showCancelButton: true,
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.delete(route('users.delete', { user: state.value.id }), {}).then(response => {
                            if (response.data.success === true) {
                                bus.$emit('alert.success', 'Success')
                                router.push({name: 'users.list'})
                            } else {
                                bus.$emit('alert.danger', response.data.message)
                            }
                        }).catch(e => {
                            bus.$emit('alert.danger', 'Delete failed')
                        })
                    }
                })
        }

        const passwordReset = () => {
            alert.fire({
                title: 'Confirm Reset Password',
                text: 'Confirm you wish to reset the user\'s account password. This will send an email to the user containing a link to continue the process',
                icon: 'question',
                showCancelButton: true,
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('users.reset-password', { user: state.value.id }))
                            .then((response) => {
                                if (response.status === 200) {
                                    passwordHasBeenReset.value = true
                                }
                            })
                    }
                })
        }

        const verified = () => {
            state.value.activated = true
        }

        onMounted(() => {
            window.location.hash = 'profile'
        })

        return {
            passwordReset,
            passwordHasBeenReset,
            state,
            saveState,
            blockUser,
            deleteUser,
            verified,
            loading
        }
    }
}
</script>

<style scoped>

</style>
